import React, { useState } from "react";
import "./Sidebar.css";

export interface Conversation {
  conversation_id: string;
  title?: string;
  last_message: string;
  updated_at?: string;
}

interface SidebarProps {
  realName?: string;
  conversations: Conversation[];
  activeConversationId?: string | null;
  onSelectConversation: (conversationId: string) => void;
  onNewChat?: () => void;
}

function getUserInfo() {
  try {
    const token = sessionStorage.getItem("access_token");

    if (!token) {
      return {
        username: "Unknown User",
        tenantId: "",
      };
    }

    const payload = JSON.parse(
      atob(token.split(".")[1])
    );

    return {
      username:
        payload.email ||
        payload.sub ||
        payload.username ||
        "Unknown User",

      tenantId:
        payload.tenant_id ||
        payload.tenantId ||
        "",

        tenantShortName:
         payload.tenant_short_name ||
        "Unknown Tenant Short Name", 
    };
  } catch (err) {
    console.error("Unable to decode JWT", err);

    return {
      username: "Unknown User",
      tenantId: "",
    };
  }
}

const Sidebar: React.FC<SidebarProps> = ({
  conversations,
  activeConversationId,
  onSelectConversation,
  onNewChat,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  const userInfo = getUserInfo();

  const logout = () => {
    // Clear session
    sessionStorage.clear();

    // Remove any old Cognito entries if they still exist
    Object.keys(localStorage)
      .filter((k) =>
        k.includes("CognitoIdentityServiceProvider")
      )
      .forEach((k) => localStorage.removeItem(k));

    window.location.href = "/login";
  };

  return (
    <aside
      className={`sidebar ${
        collapsed ? "collapsed" : ""
      }`}
    >
      {/* ───────── Header (Logo + Toggle) ───────── */}
      <div className="sidebar-header">
        {!collapsed && (
          <img
            src="/emite-logo-black-2.png"
            className="logo"
            alt="eMite"
          />
        )}

        <button
          className="sidebar-toggle-btn"
          onClick={() =>
            setCollapsed(!collapsed)
          }
        >
          <img
            src="/bt_sidebar.png"
            className={`sidebar-toggle-icon ${
              collapsed ? "rotated" : ""
            }`}
            alt="Toggle sidebar"
          />
        </button>
      </div>

      {/* ───────── New Chat ───────── */}
      {!collapsed && onNewChat && (
        <div className="sidebar-actions">
          <button
            className="new-chat-btn"
            onClick={onNewChat}
          >
            New chat
          </button>
        </div>
      )}

      {/* ───────── Conversation List ───────── */}
      {!collapsed && (
        <div className="conversation-list">
          {conversations.length === 0 && (
            <div className="empty-state">
              No conversations yet
            </div>
          )}

          {conversations.map((conv) => {
            const isActive =
              conv.conversation_id ===
              activeConversationId;

            return (
              <div
                key={conv.conversation_id}
                className={`conversation-item ${
                  isActive ? "active" : ""
                }`}
                onClick={() =>
                  onSelectConversation(
                    conv.conversation_id
                  )
                }
              >
                <div className="conversation-title">
                  {conv.title ?? "New chat"}
                </div>

                <div className="conversation-preview">
                  {conv.last_message}
                </div>

                {/* Uncomment if you want timestamps */}
                {/*
                {conv.updated_at && (
                  <div className="conversation-time">
                    {new Date(
                      conv.updated_at
                    ).toLocaleString()}
                  </div>
                )}
                */}
              </div>
            );
          })}
        </div>
      )}

      {/* ───────── Bottom (User + Logout) ───────── */}
      <div className="sidebar-bottom">
        {!collapsed && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              width: "100%",
            }}
          >
            <span className="pill">
              &nbsp;
            </span>
            <span className="pill">
              Tenant Name: {userInfo.tenantShortName}
            </span>
            <span className="pill">
              {userInfo.username}
            </span>
          </div>
        )}

        <button
          className="logout-btn"
          onClick={logout}
        >
          {collapsed ? "⎋" : "Logout"}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;